import React, { useEffect, useState } from 'react';
import { Text, StyleSheet, Pressable, FlatList, Platform, ViewStyle } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Title } from '@constants/Themed';
import AnimatedLottieView from '@utils/lottieAnimation';
import * as Animatable from 'react-native-animatable';
import apis from '@api/api';
import { useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { LinearGradient } from 'expo-linear-gradient';
import UserListItem from '../../UsersScreen/Components/UserListItem';
import { CountUp } from "use-count-up";
import { FontAwesome, MaterialIcons } from '@expo/vector-icons';
import styles from '@stylesheet';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { SET_DATAFILTER } from '@reduxLocal/actions/types';
import { Progress } from 'tamagui';
import useGlobalUser from '@hooks/getGlobalUser';
import { View, H2, H4, H5, XStack, H6, SizableText } from 'tamagui';

type SalesStatsProps = {
  style?: ViewStyle;
  loadingCallback?: () => void;
}

const SalesStats = ({ style, loadingCallback }: SalesStatsProps) => {
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const navigation = useNavigation();
  const theme = themeSelector();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const campaignData = queryClient.getQueryData(['campaignData']);
  const companyMembers = queryClient.getQueryData(['companyMembers']);
  
  function getColorByPercentage(percentage: number) {
    if (percentage >= 75) {
      return theme ? '#10b981' : '#34d399'; // Green
    } else if (percentage <= 25) {
      return theme ? '#ef4444' : '#f87171'; // Red
    } else {
      return theme ? '#f59e0b' : '#fbbf24'; // Amber
    }
  }

  const getStatCardStyle = (index: number) => ({
    success: { 
      bg: theme ? '#dcfce7' : '#064e3b', 
      color: theme ? '#166534' : '#6ee7b7',
      icon: 'trending-up' as const
    },
    warning: { 
      bg: theme ? '#fef3c7' : '#451a03', 
      color: theme ? '#d97706' : '#fcd34d',
      icon: 'assessment' as const
    },
    info: { 
      bg: theme ? '#dbeafe' : '#0c4a6e', 
      color: theme ? '#2563eb' : '#60a5fa',
      icon: 'analytics' as const
    },
    primary: { 
      bg: theme ? '#ede9fe' : '#312e81', 
      color: theme ? '#7c3aed' : '#a78bfa',
      icon: 'campaign' as const
    },
    danger: { 
      bg: theme ? '#fecaca' : '#7f1d1d', 
      color: theme ? '#dc2626' : '#fb7185',
      icon: 'people' as const
    }
  }[['success', 'info', 'primary', 'warning', 'danger'][index % 5]]);



  const { data: salesStats, isLoading, error } = useQuery({
    queryKey: ["salesStats"],
    queryFn: async () => {
      const salesStats = await apis.getSalesStats().then(res => res.data);
      return salesStats;
    }
  })

  const { data: crmCount, isLoading: crmLoading } = useQuery({
    queryKey: ["crmCount"],
    queryFn: async () => {
      const crmCount = await apis.getCrmCount();
      return crmCount;
    }
  })

  useEffect(() => {
    if (!isLoading && !crmLoading) { loadingCallback?.(); }
  }, [crmLoading, isLoading])

  return (
    <View style={style}>
      {isLoading ? (
        <Animatable.View
          animation="fadeIn"
          style={[modernStyles.loadingContainer, {
            backgroundColor: theme ? "#ffffff" : "#1e293b",
            borderColor: theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)"
          }]}
        >
          <AnimatedLottieView 
            source={require('@assets/animations/leaf_Loader.json')} 
            style={{ width: 100, height: 100 }} 
            autoPlay 
            loop 
          />
          <Text style={[modernStyles.loadingText, { color: theme ? "#64748b" : "#94a3b8" }]}>
            Loading Sales Dashboard
          </Text>
          <Progress />
        </Animatable.View>
      ) : (
        <XStack flex={1} flexWrap="wrap" gap={16} justifyContent="center">
          {/* Sales Staff Card */}
          {currentCompany && (
            <Animatable.View
              animation="fadeInUp"
              delay={0}
              style={[modernStyles.staffContainer, {
                backgroundColor: theme ? "#ffffff" : "#1e293b",
                borderColor: theme ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)"
              }]}
            >
              <View style={modernStyles.headerSection}>
                <View style={[modernStyles.iconContainer, {
                  backgroundColor: theme ? "#ddd6fe" : "#312e81"
                }]}>
                  <MaterialIcons
                    name="people"
                    size={28}
                    color={theme ? "#7c3aed" : "#a78bfa"}
                  />
                </View>
                <View style={modernStyles.headerText}>
                  <H5 style={[modernStyles.cardTitle, { color: theme ? "#1e293b" : "#f1f5f9" }]}>
                    Sales Staff
                  </H5>
                  <Text style={[modernStyles.cardSubtitle, { color: theme ? "#64748b" : "#94a3b8" }]}>
                    Top performers 🚀
                  </Text>
                </View>
              </View>
              
              <View style={modernStyles.staffList}>
                {companyMembers && Array.isArray(companyMembers) ? (
                  <FlatList
                    data={companyMembers as any[]}
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={{ gap: 12, paddingHorizontal: 4 }}
                    renderItem={({ item, index }) => (
                      <Pressable
                        style={modernStyles.staffItem}
                        onPress={() => (navigation as any).navigate('SalesStats', { 
                          id: item._id, 
                          params: { user: item } 
                        })}
                      >
                        <UserListItem
                          removePermission
                          textColor={theme ? "#1e293b" : "#f1f5f9"}
                          hideEmail
                          avatarImageStyle={modernStyles.staffAvatar}
                          item={item.user}
                          index={index}
                        />
                      </Pressable>
                    )}
                  />
                ) : (
                  <View style={modernStyles.emptyState}>
                    <MaterialIcons
                      name="people-outline"
                      size={32}
                      color={theme ? "#94a3b8" : "#64748b"}
                    />
                    <Text style={[modernStyles.emptyText, { color: theme ? "#64748b" : "#94a3b8" }]}>
                      No staff members
                    </Text>
                  </View>
                )}
              </View>
            </Animatable.View>
          )}

          {/* Stats Cards */}
          {[
            {
              title: "Campaigns",
              value: (campaignData as any)?.length || 0,
              onPress: () => (navigation as any).navigate('ActiveCampaigns'),
              index: 0
            },
            {
              title: "Conversions",
              value: "85%",
              percentage: 85,
              index: 1
            },
            {
              title: "CRM Size",
              value: crmCount?.count || 0,
              isLoading: crmLoading,
              onPress: () => {
                dispatch({ type: SET_DATAFILTER, payload: "customers" });
                (navigation as any).navigate('Data', { params: "DataBrowserScreen" });
              },
              index: 2
            },
            {
              title: "Click Rate",
              value: "95%",
              percentage: 95,
              index: 3
            }
          ].map((stat, index) => {
            const cardStyle = getStatCardStyle(index);
            return (
              <Animatable.View
                key={index}
                animation="bounceIn"
                delay={index * 150}
                style={[modernStyles.statCard, {
                  backgroundColor: cardStyle.bg,
                  borderColor: theme ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)"
                }]}
              >
                <Pressable
                  style={({ pressed }) => [
                    modernStyles.statCardContent,
                    {
                      backgroundColor: pressed ? (theme ? "rgba(0,0,0,0.02)" : "rgba(255,255,255,0.05)") : "transparent"
                    }
                  ]}
                  onPress={stat.onPress}
                  disabled={!stat.onPress}
                >
                  <View style={modernStyles.statHeader}>
                    <MaterialIcons
                      name={cardStyle.icon}
                      size={24}
                      color={cardStyle.color}
                    />
                    <Text numberOfLines={1} adjustsFontSizeToFit style={[modernStyles.statTitle, { color: cardStyle.color }]}>
                      {stat.title}
                    </Text>
                  </View>

                  <View style={modernStyles.statValueContainer}>
                    {stat.isLoading ? (
                      <AnimatedLottieView
                        source={require('@assets/animations/leaf_Loader.json')}
                        style={{ width: 40, height: 40 }}
                        autoPlay
                        loop
                      />
                    ) : (
                      <Text style={[modernStyles.statValue, { color: cardStyle.color }]}>
                        {typeof stat.value === 'string' ? stat.value : (
                          <CountUp isCounting end={stat.value as number} duration={2} />
                        )}
                      </Text>
                    )}
                  </View>
                </Pressable>
              </Animatable.View>
            );
          })}
        </XStack>
      )}
    </View>
  );
}

const modernStyles = StyleSheet.create({
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    borderRadius: 20,
    shadowOffset: { width: 0, height: 8 },
    shadowRadius: 16,
    shadowOpacity: 0.1,
    elevation: 8,
    borderWidth: 1,
    gap: 20,
    minHeight: 200,
  },

  loadingText: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },

  staffContainer: {
    borderRadius: 20,
    padding: 20,
    shadowOffset: { width: 0, height: 8 },
    shadowRadius: 16,
    shadowOpacity: 0.08,
    elevation: 8,
    borderWidth: 1,
    minWidth: Platform.OS === 'web' ? 350 : '100%',
    maxWidth: Platform.OS === 'web' ? '45%' : '100%',
    minHeight: 180,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(10px)',
      }
    })
  },

  headerSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 12,
  },

  iconContainer: {
    width: 50,
    height: 50,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },

  headerText: {
    flex: 1,
  },

  cardTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },

  cardSubtitle: {
    fontSize: 14,
    fontWeight: '500',
  },

  staffList: {
    flex: 1,
    minHeight: 100,
  },

  staffItem: {
    width: 100,
    alignItems: 'center',
  },

  staffAvatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
    borderWidth: 2,
    borderColor: 'transparent',
  },

  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },

  emptyText: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
  },

  statCard: {
    borderRadius: 20,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 6 },
    shadowRadius: 12,
    shadowOpacity: 0.08,
    elevation: 6,
    minWidth: Platform.OS === 'web' ? 180 : 150,
    maxWidth: Platform.OS === 'web' ? 220 : '45%',
    flexBasis: Platform.OS === 'web' ? '22%' : '45%',
    flexGrow: 1,
    minHeight: 140,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        backdropFilter: 'blur(8px)',
      }
    })
  },

  statCardContent: {
    padding: 20,
    flex: 1,
    borderRadius: 20,
    justifyContent: 'space-between',
  },

  statHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },

  statTitle: {
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
  },

  statValueContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },

  statValue: {
    fontSize: 32,
    fontWeight: '800',
    textAlign: 'center',
    lineHeight: 38,
  },
});

export default SalesStats;